import styled from '@emotion/styled';
import { Drawer, DrawerHeader } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import {
  ContentWithImage,
  DrawerSection,
  PercentageSection,
  TranslatorComponent,
} from './components';
import { useDrawerNavigation } from './hooks/use-drawer-navigation';
import weeklyPracticeImage from './img/weekly-practice-drawer-image-1.png';
import weeklyPracticeImage2 from './img/weekly-practice-drawer-image-2.png';

const Container = styled.div`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text.text.value};
  display: flex;
  width: 584px;
  padding: 16px 32px 32px 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

type MoreInfoDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onOpenModifyModal?: () => void;
};

const MoreInfoDrawer = ({
  isOpen,
  onClose,
  onOpenModifyModal,
}: MoreInfoDrawerProps): JSX.Element => {
  const { t } = useTranslation();
  const { handlePersonalizeLinkClick, handleReportLinkClick } =
    useDrawerNavigation({
      onOpenModifyModal,
    });

  return (
    <Drawer
      closeOnClickOutside
      key="unblocked-applets-drawer"
      open={isOpen}
      roundedBorder
      onClose={onClose}
    >
      <Container>
        <DrawerHeader
          title={t('homeWeeklyPractice.drawer1')}
          onCloseDrawer={onClose}
        />
        <ContentWrapper>
          <DrawerSection bodyTranslationKey="homeWeeklyPractice.drawer2" />
          <DrawerSection
            titleTranslationKey="homeWeeklyPractice.drawer3"
            isQuestionSection
          >
            <ContentWithImage
              imageSrc={weeklyPracticeImage}
              imageAlt="Weekly practice"
            >
              <TranslatorComponent translationKey="homeWeeklyPractice.drawer4" />
            </ContentWithImage>
          </DrawerSection>
          <Divider />
          <DrawerSection
            titleTranslationKey="homeWeeklyPractice.drawer5"
            bodyTranslationKey="homeWeeklyPractice.drawer6"
            isQuestionSection
          >
            <PercentageSection
              percentage={t('homeWeeklyPractice.70percent')}
              titleKey="homeWeeklyPractice.drawer7"
              bodyTranslationKey="homeWeeklyPractice.drawer8"
              announcementTranslationKey="homeWeeklyPractice.drawer9"
            />
            <PercentageSection
              percentage={t('homeWeeklyPractice.30percent')}
              title={t('homeWeeklyPractice.drawer10')}
              bodyTranslationKey="homeWeeklyPractice.drawer11"
              announcementTranslationKey="homeWeeklyPractice.drawer12"
            />
          </DrawerSection>
          <Divider />
          <DrawerSection
            titleTranslationKey="homeWeeklyPractice.drawer13"
            isQuestionSection
          >
            <DrawerSection
              bodyTranslationKey="homeWeeklyPractice.drawer14"
              onPersonalizeLinkClick={handlePersonalizeLinkClick}
            />
            <DrawerSection bodyTranslationKey="homeWeeklyPractice.drawer14.1" />
            <ContentWithImage
              imageSrc={weeklyPracticeImage2}
              imageAlt="Weekly practice"
            >
              <TranslatorComponent
                translationKey="homeWeeklyPractice.drawer14.2"
                onReportLinkClick={handleReportLinkClick}
              />
            </ContentWithImage>
          </DrawerSection>
          <Divider />
          <DrawerSection
            titleTranslationKey="homeWeeklyPractice.drawer15"
            bodyTranslationKey="homeWeeklyPractice.drawer16"
            announcementTranslationKey="homeWeeklyPractice.drawer17"
            announcementType="warning"
          />
        </ContentWrapper>
      </Container>
    </Drawer>
  );
};

export default MoreInfoDrawer;
